import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Checkbox, Input, SearchableSelect } from "@/shared/ui";
import { useGetRegionsQuery, useGetCitiesQuery, useGetCitiesByRegionQuery } from "@/shared/api/common";
import type { RegionResponse, CityByRegionResponse } from "@/shared/api/common";
import { formatAddressFromParts, parseAddressString } from "@/shared/utils";
import type { AddressesSection } from "../../../types/personalSheet";
import type { SectionFormHandle } from "./types";

interface Props {
  addresses: AddressesSection | null;
  draftValues?: AddressesSection | null;
}

interface RegionOption {
  id: number;
  name: string;
}

interface CityOption {
  id: number;
  name: string;
}

const addressBlockSchema = z
  .object({
    region_id: z.number({ message: "validation.addresses.region_required" }),
    region_city_type: z.enum(["region", "national_city"], { message: "validation.addresses.region_required" }),
    city_id: z.number().optional(),
    street: z.string().min(1, { message: "validation.addresses.street_required" }),
    home_number: z.string().min(1, { message: "validation.addresses.home_required" }),
    apartment_number: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.region_city_type === "region" && (data.city_id === undefined || Number.isNaN(data.city_id))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "validation.addresses.city_required",
        path: ["city_id"],
      });
    }
  });

const addressesSchema = z.object({ registration: addressBlockSchema, factual: addressBlockSchema });
type AddressesFormValues = z.infer<typeof addressesSchema>;

function makeEmptyBlock(): AddressesFormValues["registration"] {
  return {
    region_id: undefined as unknown as number,
    region_city_type: "region",
    city_id: undefined,
    street: "",
    home_number: "",
    apartment_number: "",
  };
}

const EditAddressesSection = forwardRef<SectionFormHandle, Props>(function EditAddressesSection({ addresses, draftValues }, ref) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const locale = (i18n.language as "ru" | "kk" | "en") || "ru";

  const [sameAsRegistration, setSameAsRegistration] = useState(false);

  const { data: regions = [] } = useGetRegionsQuery();
  const { data: allCities = [] } = useGetCitiesQuery();

  const initialRef = useRef({
    address_registration: addresses?.address_registration ?? "",
    address_factual: addresses?.address_factual ?? "",
  });

  const displayRef = useRef({
    address_registration: (draftValues ?? addresses)?.address_registration ?? "",
    address_factual: (draftValues ?? addresses)?.address_factual ?? "",
  });

  const {
    register,
    reset,
    setValue,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<AddressesFormValues>({
    resolver: zodResolver(addressesSchema),
    mode: "onTouched",
    defaultValues: { registration: makeEmptyBlock(), factual: makeEmptyBlock() },
  });

  const registrationRegionId = watch("registration.region_id");
  const factualRegionId = watch("factual.region_id");
  const registrationRegion = regions.find((r) => r.id === registrationRegionId);
  const factualRegion = regions.find((r) => r.id === factualRegionId);
  const shouldShowRegistrationCity = registrationRegion?.city_type === "region";
  const shouldShowFactualCity = factualRegion?.city_type === "region";

  const { data: registrationCities = [], isLoading: isRegistrationCitiesLoading } = useGetCitiesByRegionQuery(
    registrationRegionId,
    { skip: !shouldShowRegistrationCity || typeof registrationRegionId !== "number" },
  );
  const { data: factualCities = [], isLoading: isFactualCitiesLoading } = useGetCitiesByRegionQuery(factualRegionId, {
    skip: !shouldShowFactualCity || typeof factualRegionId !== "number",
  });

  useEffect(() => {
    if (!registrationRegionId) return;
    if (registrationRegion?.city_type === "national_city") {
      setValue("registration.city_id", undefined, { shouldValidate: true });
    }
  }, [registrationRegionId, registrationRegion?.city_type, setValue]);

  useEffect(() => {
    if (!factualRegionId) return;
    if (factualRegion?.city_type === "national_city") {
      setValue("factual.city_id", undefined, { shouldValidate: true });
    }
  }, [factualRegionId, factualRegion?.city_type, setValue]);

  const dataLoadedRef = useRef(false);
  useEffect(() => {
    if (regions.length === 0 || dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    const regParsed = parseAddressString(displayRef.current.address_registration);
    const factParsed = parseAddressString(displayRef.current.address_factual);

    reset({
      registration: {
        ...makeEmptyBlock(),
        street: regParsed?.street ?? "",
        home_number: regParsed?.home_number ?? "",
        apartment_number: regParsed?.apartment_number ?? "",
      },
      factual: {
        ...makeEmptyBlock(),
        street: factParsed?.street ?? "",
        home_number: factParsed?.home_number ?? "",
        apartment_number: factParsed?.apartment_number ?? "",
      },
    });

    function resolveCity(cityName: string | undefined, block: "registration" | "factual") {
      if (!cityName) return;
      const normalized = cityName.trim().toLowerCase();
      const national = regions.find(
        (r) =>
          r.city_type === "national_city" &&
          [r.name_ru, r.name_kk, r.name_en].some((n) => (n ?? "").trim().toLowerCase() === normalized),
      );
      if (national) {
        setValue(`${block}.region_id`, national.id, { shouldValidate: true });
        setValue(`${block}.region_city_type`, "national_city", { shouldValidate: true });
        setValue(`${block}.city_id`, undefined, { shouldValidate: true });
        return;
      }
      const city = allCities.find((c) =>
        [c.name_ru, c.name_kk, c.name_en].filter(Boolean).some((n) => (n ?? "").trim().toLowerCase() === normalized),
      );
      if (city?.parent_region) {
        setValue(`${block}.region_id`, city.parent_region, { shouldValidate: true });
        setValue(`${block}.region_city_type`, "region", { shouldValidate: true });
        setValue(`${block}.city_id`, city.id, { shouldValidate: true });
      }
    }

    resolveCity(regParsed?.city, "registration");
    resolveCity(factParsed?.city, "factual");
  }, [regions, allCities, reset, setValue]);

  function localizedName(item: { name_ru: string; name_kk: string; name_en: string }) {
    return (locale === "kk" ? item.name_kk : locale === "en" ? item.name_en : item.name_ru) ?? item.name_ru;
  }

  function buildBlockString(block: "registration" | "factual", cities: CityByRegionResponse[]): string {
    const values = getValues(block);
    const region = regions.find((r) => r.id === values.region_id);
    if (!region || !values.street?.trim() || !values.home_number?.trim()) return "";
    const city: RegionResponse | CityByRegionResponse | undefined =
      region.city_type === "national_city" ? region : cities.find((c) => c.id === values.city_id);
    return formatAddressFromParts({
      city: city ? localizedName(city) : "",
      street: values.street,
      home_number: values.home_number,
      apartment_number: values.apartment_number,
    });
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        const reg = buildBlockString("registration", registrationCities);
        const fact = buildBlockString("factual", factualCities);
        const changes: Record<string, unknown> = {};
        if (reg && reg !== initialRef.current.address_registration) changes.address_registration = reg;
        if (fact && fact !== initialRef.current.address_factual) changes.address_factual = fact;
        return Object.keys(changes).length > 0 ? changes : null;
      },
      getFiles() {
        return null;
      },
      async validate() {
        const results: boolean[] = [];
        if (getValues("registration.region_id")) results.push(await trigger("registration"));
        if (getValues("factual.region_id")) results.push(await trigger("factual"));
        return results.every(Boolean);
      },
      isDirty() {
        const reg = buildBlockString("registration", registrationCities);
        const fact = buildBlockString("factual", factualCities);
        return (
          (reg !== "" && reg !== initialRef.current.address_registration) ||
          (fact !== "" && fact !== initialRef.current.address_factual)
        );
      },
    };
  });

  const regionOptions: RegionOption[] = regions.map((r) => ({ id: r.id, name: localizedName(r) }));
  const registrationCityOptions: CityOption[] = registrationCities.map((c) => ({ id: c.id, name: localizedName(c) }));
  const factualCityOptions: CityOption[] = factualCities.map((c) => ({ id: c.id, name: localizedName(c) }));

  const selectedRegistrationRegion = regionOptions.find((r) => r.id === registrationRegionId) ?? null;
  const selectedFactualRegion = regionOptions.find((r) => r.id === factualRegionId) ?? null;
  const selectedRegistrationCity = registrationCityOptions.find((c) => c.id === watch("registration.city_id")) ?? null;
  const selectedFactualCity = factualCityOptions.find((c) => c.id === watch("factual.city_id")) ?? null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="flex flex-col gap-4">
        <p className="text-body-bold-lg content-base-primary">{t("personalSheet.fields.addressRegistration")}</p>

        <SearchableSelect<RegionOption>
          label={tApply("addresses.region.label")}
          hasRequiredLabel
          placeholder={tApply("addresses.region.placeholder")}
          options={regionOptions}
          value={selectedRegistrationRegion}
          onChange={(opt) => {
            if (!opt) return;
            const region = regions.find((r) => r.id === opt.id);
            setValue("registration.region_id", opt.id, { shouldValidate: true });
            setValue("registration.region_city_type", region?.city_type === "national_city" ? "national_city" : "region", {
              shouldValidate: true,
            });
            setValue("registration.city_id", undefined, { shouldValidate: true });
          }}
          searchKeys={["name"]}
          displayKey="name"
          hideSearchIcon
          error={errors.registration?.region_id?.message ? tApply(errors.registration.region_id.message) : undefined}
        />

        {shouldShowRegistrationCity && (
          <SearchableSelect<CityOption>
            label={tApply("addresses.city.label")}
            placeholder={tApply("addresses.city.placeholder")}
            options={registrationCityOptions}
            value={selectedRegistrationCity}
            onChange={(opt) => setValue("registration.city_id", opt ? opt.id : undefined, { shouldValidate: true })}
            searchKeys={["name"]}
            displayKey="name"
            hideSearchIcon
            disabled={isRegistrationCitiesLoading}
            error={errors.registration?.city_id?.message ? tApply(errors.registration.city_id.message) : undefined}
          />
        )}

        <Input
          label={tApply("addresses.street.label")}
          hasRequiredLabel
          placeholder={tApply("addresses.street.placeholder")}
          {...register("registration.street")}
          error={errors.registration?.street?.message ? tApply(errors.registration.street.message) : undefined}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={tApply("addresses.home_number.label")}
            hasRequiredLabel
            placeholder={tApply("addresses.home_number.placeholder")}
            {...register("registration.home_number")}
            error={errors.registration?.home_number?.message ? tApply(errors.registration.home_number.message) : undefined}
          />
          <Input
            label={tApply("addresses.apartment_number.label")}
            placeholder={tApply("addresses.apartment_number.placeholder")}
            {...register("registration.apartment_number")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <p className="text-body-bold-lg content-base-primary">{t("personalSheet.fields.addressFactual")}</p>

        <SearchableSelect<RegionOption>
          label={tApply("addresses.region.label")}
          hasRequiredLabel
          placeholder={tApply("addresses.region.placeholder")}
          options={regionOptions}
          value={selectedFactualRegion}
          onChange={(opt) => {
            if (!opt) return;
            const region = regions.find((r) => r.id === opt.id);
            setValue("factual.region_id", opt.id, { shouldValidate: true });
            setValue("factual.region_city_type", region?.city_type === "national_city" ? "national_city" : "region", {
              shouldValidate: true,
            });
            setValue("factual.city_id", undefined, { shouldValidate: true });
          }}
          searchKeys={["name"]}
          displayKey="name"
          hideSearchIcon
          error={errors.factual?.region_id?.message ? tApply(errors.factual.region_id.message) : undefined}
        />

        {shouldShowFactualCity && (
          <SearchableSelect<CityOption>
            label={tApply("addresses.city.label")}
            placeholder={tApply("addresses.city.placeholder")}
            options={factualCityOptions}
            value={selectedFactualCity}
            onChange={(opt) => setValue("factual.city_id", opt ? opt.id : undefined, { shouldValidate: true })}
            searchKeys={["name"]}
            displayKey="name"
            hideSearchIcon
            disabled={isFactualCitiesLoading}
            error={errors.factual?.city_id?.message ? tApply(errors.factual.city_id.message) : undefined}
          />
        )}

        <Input
          label={tApply("addresses.street.label")}
          hasRequiredLabel
          placeholder={tApply("addresses.street.placeholder")}
          {...register("factual.street")}
          error={errors.factual?.street?.message ? tApply(errors.factual.street.message) : undefined}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={tApply("addresses.home_number.label")}
            hasRequiredLabel
            placeholder={tApply("addresses.home_number.placeholder")}
            {...register("factual.home_number")}
            error={errors.factual?.home_number?.message ? tApply(errors.factual.home_number.message) : undefined}
          />
          <Input
            label={tApply("addresses.apartment_number.label")}
            placeholder={tApply("addresses.apartment_number.placeholder")}
            {...register("factual.apartment_number")}
          />
        </div>

        <Checkbox
          state={sameAsRegistration ? "checked" : "unchecked"}
          onChange={() => {
            const next = !sameAsRegistration;
            setSameAsRegistration(next);
            setValue("factual", next ? { ...getValues("registration") } : makeEmptyBlock(), { shouldValidate: true });
          }}
          label={tApply("addresses.copy_from_registration")}
        />
      </div>
    </div>
  );
});

export default EditAddressesSection;
